import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { onboardingVideoOrder, type VideoStep } from '@/data/onboarding'
import { capture } from '@/services/analytics'
import { api } from '@/services/api'
import { onboardingLog } from '@/services/onboardingLog'
import { useAuthStore } from '@/stores/auth'

export const ONBOARDING_CHECKOUT_KEY = 'uig.onboarding.v1.checkout'

export type OnboardingPhase = 'welcome' | VideoStep | 'plans'

type StorageName = 'localStorage' | 'sessionStorage'

function readFlag(storage: StorageName, key: string): boolean {
  try {
    return window[storage].getItem(key) === '1'
  } catch {
    onboardingLog('warn', 'storage_unavailable', { storage, action: 'read' })
    return false
  }
}

function writeFlag(storage: StorageName, key: string, enabled: boolean): void {
  try {
    if (enabled) window[storage].setItem(key, '1')
    else window[storage].removeItem(key)
  } catch {
    onboardingLog('warn', 'storage_unavailable', { storage, action: 'write' })
  }
}

const nextPhase: Partial<Record<OnboardingPhase, OnboardingPhase>> = {
  welcome: 'video1',
  video1: 'video3',
  video3: 'plans',
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const auth = useAuthStore()
  const phase = ref<OnboardingPhase>('welcome')
  const completedBy = ref<string | null>(null)
  const completed = computed(() => {
    const identity = auth.identity
    return identity !== null && (identity.onboarding_completed || completedBy.value === identity.user_id)
  })
  const checkoutPending = ref(readFlag('sessionStorage', ONBOARDING_CHECKOUT_KEY))
  const needsOnboarding = computed(() => !completed.value)

  function advance(expected: OnboardingPhase): boolean {
    const next = nextPhase[expected]
    if (completed.value || phase.value !== expected || !next) return false
    phase.value = next
    onboardingLog('debug', 'phase_advanced', { from: expected, to: next })
    if (next === 'plans') capture('onboarding_completed')
    return true
  }

  function reset(): void {
    if (completed.value) return
    phase.value = 'welcome'
    onboardingLog('debug', '[FIX:UNI-116] flow_reset', { steps: onboardingVideoOrder })
    capture('onboarding_started')
  }

  function markCheckoutPending(): void {
    checkoutPending.value = true
    writeFlag('sessionStorage', ONBOARDING_CHECKOUT_KEY, true)
    onboardingLog('debug', 'checkout_pending')
  }

  function clearCheckoutPending(): void {
    checkoutPending.value = false
    writeFlag('sessionStorage', ONBOARDING_CHECKOUT_KEY, false)
  }

  function complete(reason: 'close' | 'confirmed_checkout'): boolean {
    const userId = auth.identity?.user_id
    if (completed.value || !userId) return false
    completedBy.value = userId
    clearCheckoutPending()
    onboardingLog('info', 'flow_completed', { reason })
    api.completeOnboarding().catch(() => onboardingLog('warn', 'completion_sync_failed'))
    return true
  }

  return {
    phase,
    completed,
    checkoutPending,
    needsOnboarding,
    advance,
    reset,
    markCheckoutPending,
    clearCheckoutPending,
    complete,
  }
})
